/**
 * CourtageAiIntake — « Projet express (IA) » : texte libre → mandat structuré,
 * catégorisé, qualifié, rapproché de l'Annuaire, avec métacognition (suggestions
 * d'actions dans les autres modules Noam).
 */
import { useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import {
  X, Loader2, Sparkles, Wand2, MapPin, Building, Gauge, Lightbulb,
  Link as LinkIcon, ChevronLeft, Plus, AlertTriangle, FileUp, TrendingUp,
  Handshake, Users,
} from "lucide-react";

const STADE_LABELS = { etude: "Étude", permis: "Permis", chantier: "Chantier", livre: "Livré", exploitation: "Exploitation" };
const BIEN_LABELS = { residentiel: "Résidentiel", commercial: "Commercial", mixte: "Mixte", hotelier: "Hôtelier", terrain: "Terrain", autre: "Autre" };
const MODULE_LABELS = {
  annuaire: "Annuaire", risques: "Risques", financement: "Financement",
  credit_rating: "Notation crédit", origination: "Origination", splc: "SPLC", chat_interne: "Chat",
};

const fmtChf = (n) => (n ? Math.round(Number(n)).toLocaleString("fr-CH").replace(/,/g, "'") + " CHF" : "—");

const GRADE_COLOR = (note) => {
  const c = (note || "").charAt(0);
  return c === "A" ? "#1D9E75" : c === "B" ? "#378ADD" : c === "C" ? "#EF9F27" : "#E24B4A";
};

export default function CourtageAiIntake({ onClose, onCreated }) {
  const [step, setStep] = useState("input"); // input | review | batch
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [batch, setBatch] = useState([]); // [{name, jobId, status, result, selected}]
  const [draft, setDraft] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [creating, setCreating] = useState(false);

  const pollJob = async (jobId) => {
    for (let i = 0; i < 80; i++) {
      await new Promise((res) => setTimeout(res, 3000));
      const s = await api.get(`/workspace/courtage/ai-intake/${jobId}`);
      if (s.data.status === "done") return s.data.result;
      if (s.data.status === "failed") throw new Error(s.data.error || "Analyse échouée.");
    }
    throw new Error("L'analyse prend trop de temps.");
  };

  const analyze = async () => {
    if (analyzing) return;
    if (files.length === 0 && description.trim().length < 20) return;
    setAnalyzing(true);
    try {
      if (files.length > 1) {
        // Mode portefeuille : un job par PDF, analyses en parallèle
        const entries = [];
        for (const f of files) {
          const fd = new FormData();
          fd.append("file", f);
          const r = await api.post("/workspace/courtage/ai-intake-file", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          entries.push({ name: f.name, jobId: r.data.job_id, status: "running", result: null, selected: true });
        }
        setBatch(entries);
        setStep("batch");
        await Promise.all(entries.map(async (e, idx) => {
          try {
            const result = await pollJob(e.jobId);
            setBatch((prev) => prev.map((b, i) => (i === idx ? { ...b, status: "done", result } : b)));
          } catch (err) {
            setBatch((prev) => prev.map((b, i) => (i === idx ? { ...b, status: "failed", error: err.message } : b)));
          }
        }));
        setAnalyzing(false);
        return;
      }
      let r;
      if (files.length === 1) {
        const fd = new FormData();
        fd.append("file", files[0]);
        r = await api.post("/workspace/courtage/ai-intake-file", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        r = await api.post("/workspace/courtage/ai-intake", { description: description.trim() });
      }
      const result = await pollJob(r.data.job_id);
      setDraft(result);
      setStep("review");
      setAnalyzing(false);
    } catch (e) {
      notify.error(fmtError(e) || e.message);
      setAnalyzing(false);
    }
  };

  const createFromResult = async (result) => {
    const r = await api.post("/workspace/courtage/mandates", {
      title: result.title,
      type: result.type,
      statut: result.statut,
      client: result.client || "",
      contrepartie: result.contrepartie || "",
      valeur_transaction_chf: Number(result.valeur_transaction_chf) || 0,
      commission_pct: Number(result.commission_pct) || 3,
      notes: result.notes || "",
      specialties: result.specialties || [],
      immo: result.immo || null,
      annuaire_entity_ids: (result.annuaire_matches || []).map((m) => m.id),
    });
    const mid = r.data.id;
    if (result.qualification) {
      await api.put(`/workspace/courtage/mandates/${mid}/qualification`, {
        motivation_vendeur: Number(result.qualification.motivation_vendeur) || 5,
        alignement_prix_marche: Number(result.qualification.alignement_prix_marche) || 5,
        acheteurs_identifies: Number(result.qualification.acheteurs_identifies) || 0,
        complexite: Number(result.qualification.complexite) || 5,
        liquidite_marche: Number(result.qualification.liquidite_marche) || 5,
        notes: result.qualification.notes || "",
      }).catch(() => {});
    }
    await api.patch(`/workspace/courtage/mandates/${mid}`, {
      metacognition: result.metacognition || [],
      market_analysis: result.market_analysis || null,
      project_grade: result.project_grade || null,
      negotiation: result.negotiation || null,
      annuaire_buyers: result.annuaire_buyers || [],
      buyer_profiles: result.buyer_profiles || [],
    }).catch(() => {});
    return mid;
  };

  const createBatch = async () => {
    const chosen = batch.filter((b) => b.status === "done" && b.selected);
    if (chosen.length === 0 || creating) return;
    setCreating(true);
    try {
      for (const b of chosen) await createFromResult(b.result);
      notify.success(`${chosen.length} projet${chosen.length > 1 ? "s" : ""} créé${chosen.length > 1 ? "s" : ""}, catégorisé${chosen.length > 1 ? "s" : ""} et qualifié${chosen.length > 1 ? "s" : ""}.`);
      onCreated(null);
    } catch (e) { notify.error(fmtError(e)); }
    finally { setCreating(false); }
  };

  const create = async () => {
    if (!draft || creating) return;
    const name = (draft.title || "").trim();
    if (!name) { notify.error("Titre requis."); return; }
    setCreating(true);
    try {
      const mid = await createFromResult(draft);
      notify.success("Projet créé, catégorisé et qualifié.");
      onCreated(mid);
    } catch (e) { notify.error(fmtError(e)); }
    finally { setCreating(false); }
  };

  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="noam-card w-full max-w-2xl max-h-[92vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="court-ai-intake-modal">
        <div className="flex items-center justify-between mb-3">
          <div className="text-base font-semibold noam-display flex items-center gap-2">
            <Wand2 className="w-4 h-4 text-[#7F77DD]" /> Projet express (IA)
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="court-ai-intake-close"><X className="w-4 h-4" /></button>
        </div>

        {step === "input" && (
          <>
            <p className="text-xs text-zinc-400 mb-3">
              Déposez le dossier PDF du projet <b>ou</b> décrivez-le en langage libre. L'IA reconnaît le bien,
              calcule le marché, lui attribue une note, identifie des acheteurs dans votre Annuaire et
              propose des conditions de négociation.
            </p>
            <label
              className={`flex items-center justify-center gap-2 border-2 border-dashed rounded-lg px-4 py-5 mb-3 cursor-pointer transition ${files.length > 0 ? "border-[#1D9E75]/60 bg-[#1D9E75]/5 text-[#1D9E75]" : "border-white/15 text-zinc-500 hover:border-[#7F77DD]/50 hover:text-zinc-300"}`}
              data-testid="court-ai-intake-dropzone"
            >
              <FileUp className="w-4 h-4" />
              <span className="text-xs">
                {files.length === 0
                  ? "Déposer un ou plusieurs dossiers PDF (portefeuille entier accepté, max 15 Mo/fichier)"
                  : files.length === 1
                    ? `${files[0].name} — prêt à analyser`
                    : `${files.length} dossiers prêts — analyse en parallèle`}
              </span>
              <input type="file" accept=".pdf" multiple className="hidden" onChange={(e) => setFiles(Array.from(e.target.files || []))} data-testid="court-ai-intake-file" />
            </label>
            {files.length > 0 && (
              <div className="mb-2 space-y-0.5">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center justify-between text-[11px] text-zinc-400 bg-black/30 rounded px-2 py-1">
                    <span className="truncate">{f.name}</span>
                    <button onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))} className="text-zinc-600 hover:text-white ml-2"><X className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            )}
            <div className="text-[10px] uppercase tracking-wider text-zinc-600 text-center mb-2">— ou décrire en texte libre —</div>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={9}
              maxLength={5000}
              placeholder={"Ex : Immeuble mixte de 2 400 m² à Lausanne, quartier sous-gare, valorisé ~18 MCHF. Le propriétaire (famille Muller) souhaite vendre d'ici 12 mois. Permis en règle, 14 logements + 3 arcades commerciales louées. Deux fonds ont déjà manifesté un intérêt. Mandat de courtage exclusif envisagé à 2,5 %…"}
              className="noam-input w-full text-sm mb-1"
              data-testid="court-ai-intake-description"
            />
            <div className="text-[10px] text-zinc-600 mb-3">{description.length}/5000</div>
            <button
              onClick={analyze}
              disabled={(files.length === 0 && description.trim().length < 20) || analyzing}
              className="w-full flex items-center justify-center gap-2 text-xs px-3 py-2.5 rounded-md border border-[#7F77DD]/50 bg-[#7F77DD]/10 text-[#B5AEF7] hover:bg-[#7F77DD]/20 transition disabled:opacity-50"
              data-testid="court-ai-intake-analyze"
            >
              {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {analyzing ? "Analyse du dossier en cours (60-120 s)…" : files.length > 1 ? `Analyser les ${files.length} dossiers` : "Analyser et structurer le projet"}
            </button>
          </>
        )}

        {step === "batch" && (
          <div className="space-y-2" data-testid="court-ai-intake-batch">
            <p className="text-xs text-zinc-400">
              Analyse du portefeuille — chaque dossier est reconnu, noté et qualifié. Décochez ceux à ne pas créer.
            </p>
            {batch.map((b, i) => (
              <div key={i} className={`noam-card p-3 bg-black/30 flex items-center gap-3 ${b.status === "failed" ? "border border-[#E24B4A]/40" : ""}`} data-testid={`court-batch-item-${i}`}>
                {b.status === "done" ? (
                  <input type="checkbox" checked={b.selected} onChange={() => setBatch((prev) => prev.map((x, j) => (j === i ? { ...x, selected: !x.selected } : x)))} className="accent-[#1D9E75]" data-testid={`court-batch-check-${i}`} />
                ) : b.status === "failed" ? (
                  <AlertTriangle className="w-4 h-4 text-[#E24B4A] shrink-0" />
                ) : (
                  <Loader2 className="w-4 h-4 animate-spin text-[#7F77DD] shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-zinc-200 font-medium truncate">{b.result?.title || b.name}</div>
                  <div className="text-[10px] text-zinc-500 truncate">
                    {b.status === "running" && "Analyse en cours…"}
                    {b.status === "failed" && (b.error || "Analyse échouée")}
                    {b.status === "done" && `${b.result?.immo?.localisation || ""} · ${fmtChf(b.result?.valeur_transaction_chf)} · ${(b.result?.annuaire_buyers || []).length} acheteur(s) annuaire`}
                  </div>
                </div>
                {b.status === "done" && b.result?.project_grade?.note && (
                  <span className="text-lg font-bold noam-display shrink-0" style={{ color: GRADE_COLOR(b.result.project_grade.note) }}>
                    {b.result.project_grade.note}
                  </span>
                )}
              </div>
            ))}
            <button
              onClick={createBatch}
              disabled={creating || batch.some((b) => b.status === "running") || batch.filter((b) => b.status === "done" && b.selected).length === 0}
              className="btn-primary w-full justify-center disabled:opacity-50"
              data-testid="court-batch-create"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              {batch.some((b) => b.status === "running")
                ? `Analyses en cours (${batch.filter((b) => b.status !== "running").length}/${batch.length})…`
                : `Créer ${batch.filter((b) => b.status === "done" && b.selected).length} projet(s)`}
            </button>
          </div>
        )}

        {step === "review" && draft && (
          <div className="space-y-3" data-testid="court-ai-intake-review">
            <button onClick={() => setStep("input")} className="inline-flex items-center gap-1 text-[11px] text-zinc-500 hover:text-white">
              <ChevronLeft className="w-3 h-3" /> Modifier la description
            </button>

            <input value={draft.title} onChange={(e) => setField("title", e.target.value)} className="noam-input w-full font-semibold" data-testid="court-ai-intake-title" />

            <div className="grid grid-cols-2 gap-2">
              <input value={draft.client || ""} onChange={(e) => setField("client", e.target.value)} placeholder="Client" className="noam-input" data-testid="court-ai-intake-client" />
              <input value={draft.contrepartie || ""} onChange={(e) => setField("contrepartie", e.target.value)} placeholder="Contrepartie" className="noam-input" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Valeur transaction (CHF)</div>
                <input type="number" value={draft.valeur_transaction_chf || 0} onChange={(e) => setField("valeur_transaction_chf", parseFloat(e.target.value) || 0)} className="noam-input w-full" data-testid="court-ai-intake-value" />
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Commission (%)</div>
                <input type="number" step="0.1" value={draft.commission_pct || 3} onChange={(e) => setField("commission_pct", parseFloat(e.target.value) || 0)} className="noam-input w-full" />
              </label>
            </div>

            {/* Catégorisation */}
            <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
              <span className="px-2 py-0.5 rounded-full bg-[#378ADD]/15 border border-[#378ADD]/40 text-[#378ADD]">{draft.type}</span>
              <span className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-zinc-400">{draft.statut}</span>
              {(draft.specialties || []).map((s) => (
                <span key={s} className="px-2 py-0.5 rounded-full bg-[#EF9F27]/15 border border-[#EF9F27]/40 text-[#EF9F27]">{s.replace(/_/g, " ")}</span>
              ))}
            </div>

            {/* Fiche immo */}
            {draft.immo && (
              <div className="noam-card p-3 bg-black/30" data-testid="court-ai-intake-immo">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5"><Building className="w-3 h-3" /> Fiche projet immobilier</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                  <div><div className="text-zinc-600 text-[10px]">Localisation</div><div className="text-zinc-200 flex items-center gap-1"><MapPin className="w-3 h-3 text-[#378ADD]" />{draft.immo.localisation || "—"}</div></div>
                  <div><div className="text-zinc-600 text-[10px]">Type de bien</div><div className="text-zinc-200">{BIEN_LABELS[draft.immo.type_bien] || draft.immo.type_bien || "—"}</div></div>
                  <div><div className="text-zinc-600 text-[10px]">Surface</div><div className="text-zinc-200">{draft.immo.surface_m2 ? `${draft.immo.surface_m2} m²` : "—"}</div></div>
                  <div><div className="text-zinc-600 text-[10px]">Stade</div><div className="text-zinc-200">{STADE_LABELS[draft.immo.stade] || draft.immo.stade || "—"}</div></div>
                </div>
              </div>
            )}

            {/* Qualification pré-remplie */}
            {draft.qualification && (
              <div className="noam-card p-3 bg-black/30" data-testid="court-ai-intake-qual">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5"><Gauge className="w-3 h-3" /> Qualification pré-remplie (ajustable après création)</div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-zinc-400">
                  <span>Motivation vendeur : <b className="text-zinc-200">{draft.qualification.motivation_vendeur}/10</b></span>
                  <span>Prix vs marché : <b className="text-zinc-200">{draft.qualification.alignement_prix_marche}/10</b></span>
                  <span>Acheteurs identifiés : <b className="text-zinc-200">{draft.qualification.acheteurs_identifies}</b></span>
                  <span>Complexité : <b className="text-zinc-200">{draft.qualification.complexite}/10</b></span>
                  <span>Liquidité : <b className="text-zinc-200">{draft.qualification.liquidite_marche}/10</b></span>
                </div>
              </div>
            )}

            {/* Rapprochement annuaire */}
            {(draft.annuaire_matches || []).length > 0 && (
              <div className="text-[11px] text-zinc-400 flex flex-wrap items-center gap-1.5" data-testid="court-ai-intake-matches">
                <LinkIcon className="w-3 h-3 text-[#1D9E75]" /> Rapproché de l'Annuaire :
                {draft.annuaire_matches.map((m) => (
                  <span key={m.id} className="px-2 py-0.5 rounded-full bg-[#1D9E75]/15 border border-[#1D9E75]/40 text-[#1D9E75]">{m.name}</span>
                ))}
              </div>
            )}

            {/* Note projet + Analyse marché */}
            {(draft.project_grade || draft.market_analysis) && (
              <div className="grid sm:grid-cols-[auto_1fr] gap-3">
                {draft.project_grade && (
                  <div className="noam-card p-3 bg-black/30 text-center min-w-[110px]" data-testid="court-ai-intake-grade">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">Note projet</div>
                    <div className="text-3xl font-bold noam-display mt-1" style={{ color: GRADE_COLOR(draft.project_grade.note) }}>
                      {draft.project_grade.note}
                    </div>
                  </div>
                )}
                {draft.market_analysis && (
                  <div className="noam-card p-3 bg-black/30" data-testid="court-ai-intake-market">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 flex items-center gap-1.5"><TrendingUp className="w-3 h-3" /> Analyse marché</div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-zinc-400">
                      {draft.market_analysis.prix_m2_chf > 0 && <span>Prix/m² : <b className="text-zinc-200">{fmtChf(draft.market_analysis.prix_m2_chf)}</b></span>}
                      {draft.market_analysis.prix_m2_marche_chf && <span>Marché : <b className="text-zinc-200">{draft.market_analysis.prix_m2_marche_chf}</b></span>}
                      {draft.market_analysis.fourchette_basse_chf > 0 && <span>Fourchette : <b className="text-zinc-200">{fmtChf(draft.market_analysis.fourchette_basse_chf)} – {fmtChf(draft.market_analysis.fourchette_haute_chf)}</b></span>}
                      {draft.market_analysis.rendement_brut_pct > 0 && <span>Rdt brut : <b className="text-zinc-200">{draft.market_analysis.rendement_brut_pct}%</b></span>}
                      {draft.market_analysis.rendement_potentiel_pct > 0 && <span>Rdt potentiel : <b className="text-[#1D9E75]">{draft.market_analysis.rendement_potentiel_pct}%</b></span>}
                    </div>
                  </div>
                )}
              </div>
            )}
            {draft.project_grade?.rationale && <p className="text-[11px] text-zinc-500 -mt-1">{draft.project_grade.rationale}</p>}
            {draft.market_analysis?.commentaire && <p className="text-[11px] text-zinc-400 bg-black/30 rounded p-2.5 whitespace-pre-wrap">{draft.market_analysis.commentaire}</p>}

            {/* Acheteurs potentiels */}
            {((draft.annuaire_buyers || []).length > 0 || (draft.buyer_profiles || []).length > 0) && (
              <div className="noam-card p-3 bg-black/30" data-testid="court-ai-intake-buyers">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5"><Users className="w-3 h-3" /> À qui le vendre ?</div>
                {(draft.annuaire_buyers || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {draft.annuaire_buyers.map((b) => (
                      <span key={b.id} className="text-[11px] px-2 py-0.5 rounded-full bg-[#1D9E75]/15 border border-[#1D9E75]/40 text-[#1D9E75]" data-testid={`court-ai-buyer-${b.id}`}>
                        {b.name}{b.sector ? ` · ${b.sector}` : ""}
                      </span>
                    ))}
                  </div>
                )}
                {(draft.buyer_profiles || []).map((p, i) => (
                  <div key={i} className="text-[11px] text-zinc-400"><span className="text-zinc-200 font-medium">{p.profil}</span> — {p.pourquoi}</div>
                ))}
              </div>
            )}

            {/* Conditions de négociation */}
            {draft.negotiation && (
              <div className="noam-card p-3 bg-black/30 border border-[#378ADD]/20" data-testid="court-ai-intake-nego">
                <div className="text-[10px] uppercase tracking-wider text-[#378ADD] mb-2 flex items-center gap-1.5"><Handshake className="w-3 h-3" /> Conditions de négociation suggérées</div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-zinc-400 mb-1.5">
                  {draft.negotiation.prix_cible_chf > 0 && <span>Prix cible : <b className="text-zinc-200">{fmtChf(draft.negotiation.prix_cible_chf)}</b></span>}
                  {draft.negotiation.commission_suggeree_pct > 0 && <span>Commission : <b className="text-zinc-200">{draft.negotiation.commission_suggeree_pct}%</b></span>}
                  {draft.negotiation.exclusivite && <span>Exclusivité : <b className="text-zinc-200">{draft.negotiation.exclusivite}</b></span>}
                </div>
                {(draft.negotiation.conditions || []).length > 0 && (
                  <ul className="text-[11px] text-zinc-500 list-disc list-inside mb-1">
                    {draft.negotiation.conditions.map((c, i) => <li key={i}>{c}</li>)}
                  </ul>
                )}
                {draft.negotiation.strategie && <p className="text-[11px] text-zinc-400">{draft.negotiation.strategie}</p>}
              </div>
            )}

            <textarea value={draft.notes || ""} onChange={(e) => setField("notes", e.target.value)} rows={3} className="noam-input w-full text-sm" placeholder="Notes" data-testid="court-ai-intake-notes" />

            {/* Métacognition */}
            {(draft.metacognition || []).length > 0 && (
              <div className="noam-card p-3 border border-[#7F77DD]/25 bg-[#7F77DD]/[0.05]" data-testid="court-ai-intake-metacog">
                <div className="text-[10px] uppercase tracking-wider text-[#B5AEF7] mb-2 flex items-center gap-1.5"><Lightbulb className="w-3 h-3" /> Métacognition — et maintenant ?</div>
                <div className="space-y-1.5">
                  {draft.metacognition.map((s, i) => (
                    <div key={i} className="flex items-start gap-2 text-[11px]">
                      <span className="shrink-0 mt-0.5 px-1.5 py-0 rounded bg-white/5 border border-white/10 text-zinc-500 text-[9px] uppercase">{MODULE_LABELS[s.module] || s.module}</span>
                      <div><span className="text-zinc-200 font-medium">{s.title}</span> <span className="text-zinc-500">— {s.suggestion}</span></div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(draft.missing_info || []).length > 0 && (
              <div className="text-[11px] text-[#EF9F27] bg-[#EF9F27]/10 border border-[#EF9F27]/25 rounded px-3 py-2 flex items-start gap-1.5" data-testid="court-ai-intake-missing">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span><b>À compléter :</b> {draft.missing_info.join(" · ")}</span>
              </div>
            )}

            <button onClick={create} disabled={creating || !draft.title?.trim()} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="court-ai-intake-create">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              Créer le projet ({fmtChf(draft.valeur_transaction_chf)})
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
